import {Box, HBox} from '@buncha/components/core/view';
import {CheckIcon, Divider} from 'native-base';
import {H3Text, H5Text, H6Text} from '@buncha/components/core/text';
import React, {useCallback, useState} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import BottomSheet from '@buncha/components/composites/bottomSheet';
import {CustomFlatList} from '@buncha/components/core/list';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {StyleSheet} from 'react-native';
import {Touchable} from '@buncha/components/core/touchable';
import {useGetLocale} from '@buncha/localization/hooks';

interface Props {
  selectedCategory: string;
  storeCategories: string[];
  setCategory: React.Dispatch<React.SetStateAction<string>>;
}

const SelectCategory = (props: Props) => {
  const {selectedCategory, storeCategories, setCategory} = props;
  const theme = useColorThemeType('main');
  const muliFont = useFontTheme('muli');
  const mortiseFont = useFontTheme('mortise');
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const {title, body} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const toggelBottomSheet = useCallback(() => {
    setBottomSheetOpen(open => !open);
  }, []);

  const handleSelect = useCallback(
    (category: string) => {
      setCategory(category);
      toggelBottomSheet();
    },
    [setCategory, toggelBottomSheet],
  );

  const renderItem = useCallback(
    ({item}: {item: string}) => {
      const isSelected = item === selectedCategory;
      const color = isSelected
        ? theme?.text?.green?.[100]
        : theme?.text?.gray?.[100];
      return (
        <Touchable
          onPress={() => {
            handleSelect(item);
          }}>
          <HBox style={styles.itemContainer}>
            <H5Text style={{fontFamily: muliFont[700].normal, color: color}}>
              {item}
            </H5Text>
            <If condition={isSelected}>
              <CheckIcon color={color} />
            </If>
          </HBox>
        </Touchable>
      );
    },
    [
      handleSelect,
      muliFont,
      selectedCategory,
      theme?.text?.gray,
      theme?.text?.green,
    ],
  );

  const separator = useCallback(() => {
    return <Divider style={[styles.divider]} />;
  }, []);

  return (
    <Touchable onPress={toggelBottomSheet} style={styles.touchable}>
      <HBox
        style={[
          styles.conatiner,
          {
            borderColor: theme?.text.gray[300],
          },
        ]}>
        <H5Text style={{color: theme?.text?.gray?.[300]}}>
          {selectedCategory}
        </H5Text>
        <Box
          style={[
            styles.triangle,
            {borderBottomColor: theme?.text?.gray?.[300]},
          ]}
        />
      </HBox>
      <If condition={bottomSheetOpen}>
        <BottomSheet open={bottomSheetOpen} onClose={toggelBottomSheet}>
          <Box style={styles.headerContainer}>
            <H3Text
              style={[
                styles.alignLeft,
                {
                  fontFamily: mortiseFont[700].normal,
                  color: theme?.text?.blue?.[300],
                },
              ]}>
              {title?.changeCategory}
            </H3Text>
            <H6Text
              style={[
                styles.alignLeft,
                styles.subtitle,
                {fontFamily: muliFont[700].normal},
              ]}>
              {body?.selectedCategory}
              {selectedCategory}
            </H6Text>
          </Box>
          <Divider style={styles.divider} />
          <CustomFlatList
            style={styles.flatList}
            data={storeCategories}
            renderItem={renderItem}
            ItemSeparatorComponent={separator}
          />
        </BottomSheet>
      </If>
    </Touchable>
  );
};

export default React.memo(SelectCategory);

const styles = StyleSheet.create({
  touchable: {flex: 5},
  conatiner: {
    borderWidth: 1,
    padding: 10,
    paddingHorizontal: 18,
    borderRadius: 5,
  },
  alignLeft: {
    textAlign: 'left',
  },
  subtitle: {
    marginTop: 5,
  },
  divider: {
    marginVertical: 15,
  },
  itemContainer: {paddingVertical: 10, paddingHorizontal: 10},
  headerContainer: {width: '100%', marginTop: 15, paddingLeft: 10},
  flatList: {width: '100%'},
  triangle: {
    width: 0,
    height: 0,
    backgroundColor: 'transparent',
    borderStyle: 'solid',
    borderLeftWidth: 5,
    borderRightWidth: 5,
    borderBottomWidth: 5,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    transform: [{rotate: '180deg'}],
  },
});
